/* Atomz taxonomy - a breadcrumb for the path, one group for the next step.
 *
 * WHAT THIS IS NOT: it is not a second filter, and it does not order anything.
 * Every click ends up in the app's own `filterApplied` event with the app's own
 * `currentCategoryId`, so the app keeps selection, fetching and URL state, and
 * the Atomz admin keeps filter order. This file only decides how the taxonomy
 * is drawn.
 *
 * WHY IT EXISTS: the app ships one Category group holding the current node, its
 * children and a single "back" row, all wearing checkboxes. Underneath, though,
 * category is a drill-down on currentCategoryId and never a multi-select - see
 * az-search-page.js, "Hierarchical Category is a drill-down (currentCategoryId),
 * not a leaf `eq`". A checkbox describes an interaction that does not exist, and
 * one back row throws away a path the response spells out in full.
 *
 * THE SHAPE: the path goes in a breadcrumb above the results, and the rail
 * carries only the step the shopper can take next. An earlier draft stacked one
 * titled group per level, Zappos style. It reads well on a catalogue with eight
 * thousand shoes and several usable branches; on this one it spent three group
 * headers, three subtitles and three collapse toggles restating a path that fits
 * on one line. The cost is that switching an ancestor for its sibling is now two
 * clicks rather than one.
 *
 * WHERE THE PATH COMES FROM: the response's currentCategory carries the whole
 * breadcrumb as a string, "Apparel & Accessories > Shoes > Sneakers", and the
 * Shopify taxonomy gid encodes the same descent in its slug, aa > aa-8 > aa-8-8.
 * Read together they rebuild the full path on any load, cold deep link included
 * - which the app's own trail cannot do, because it reseeds an unfamiliar path
 * as [parent, current] and loses everything above the parent.
 *
 * SCOPE: the breadcrumb and the title run everywhere, since they sit above the
 * results rather than in the rail and cost one line at any width. The rail
 * group replaces the app's Category dropdown in the left layout on desktop
 * only; the horizontal chip bar and the mobile sheet keep the app's own
 * drill-down.
 *
 * FAILURE MODE: if the event, the grid element or the hierarchical flag is not
 * what this expects, teardown() runs and the app's own UI comes back untouched.
 */
(function () {
  'use strict';

  if (!/\/search/.test(window.location.pathname)) return;

  var MIN_WIDTH = 990;
  var COLLAPSE_AFTER = 8;

  var S = { trail: [], kids: [], on: false, more: false };

  /* ------------------------------------------------------------------ util */

  /* Taxonomy leaves arrive lowercased. The app's own capitalize() upper-cases
     every word, which turns "work and safety sneakers" into "Work And Safety
     Sneakers"; these names are long and this is where they are read most, so
     small words stay down unless they lead. */
  var SMALL = /^(a|an|and|as|at|but|by|for|in|nor|of|on|or|the|to|with|&)$/;

  function titleCase(s) {
    return String(s || '').toLowerCase().split(' ')
      .map(function (w, i) {
        if (i > 0 && SMALL.test(w)) return w;
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(' ');
  }

  function grid() { return document.querySelector('atomz-product-grid'); }

  function currentQuery() {
    try { return (new URLSearchParams(window.location.search).get('q') || '').trim(); }
    catch (e) { return ''; }
  }

  /* The app owns the number. This only mirrors it, so nothing here has to
     re-implement counting or race the app for a text node. */
  function countNode() { return document.querySelector('.az-total-results'); }

  function countText() {
    var el = countNode();
    if (!el) return '';
    var s = String(el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!s) return '';
    var m = s.match(/^([\d.,]+)\s*(.*)$/);
    if (!m) return s;
    var rest = m[2] || 'items';
    /* The app writes "1 items" whatever the count is. */
    if (m[1] === '1') rest = rest.replace(/\bitems\b/i, 'item');
    return m[1] + ' ' + rest;
  }

  /* The grid keeps the raw response, which holds more than the event does. */
  function productsPayload() {
    var g = grid();
    return (g && g.aiResponse && g.aiResponse.products) || null;
  }

  /* Rebuild the whole path from currentCategory. The names come from `path`,
     the ids from the gid slug, and the two are only trusted together when they
     agree on how deep the node is - so a taxonomy id that ever stops encoding
     its ancestry falls through to the app's own trail rather than inventing ids
     that resolve to nothing. */
  function trailFromCurrent(cur) {
    if (!cur || !cur.id || !cur.path) return null;

    var id = String(cur.id);
    var slug = id.slice(id.lastIndexOf('/') + 1);
    if (!slug) return null;

    var prefix = id.slice(0, id.length - slug.length);
    var segs = slug.split('-');
    var names = String(cur.path).split('>').map(function (s) { return s.trim(); });
    if (!segs.length || segs.length !== names.length) return null;

    return names.map(function (name, i) {
      return { id: prefix + segs.slice(0, i + 1).join('-'), name: name };
    });
  }

  /*
   * Does the app offer a Category facet, and is it on?
   *
   * Two stores answer this differently and the check has to survive both.
   *
   * Where the app renders a Category group in the rail, that group is the
   * answer: a merchant who switches Category off in the Atomz admin gets no
   * group, and must not get one from us either, because `hierarchical` and
   * `d.category` keep arriving in the response whether or not the facet is
   * listed. They drive the drill-down, not the facet list.
   *
   * Where the store renders no Category group at all, there is nothing to
   * read. atomzlocal is that store: 123 filter groups, every one an attribute
   * or vendor, and the taxonomy arrives only as nextLevelCategories in the
   * response. Requiring the group there stood the rail down permanently. So a
   * store with no Category group falls back to the app's own drill-down
   * signal, which is the caller's `hierarchical` check.
   *
   * Presence, not `.enabled`, in the first case. `.enabled` is a per-query
   * state the app clears whenever a group has no values, which is a different
   * thing from the merchant turning the facet off.
   */
  function categoryFacetOff() {
    try {
      var anyGroup = document.querySelector('.az-filter > .az-filter-button[id]');
      if (!anyGroup) return false;
      var hasCategory = document.querySelector('.az-filter:has(> .az-filter-button#category)');
      var storeUsesCategoryGroups =
        !!document.querySelector('atomz-product-grid[data-active-filters*="category"]');
      return storeUsesCategoryGroups && !hasCategory;
    } catch (e) {
      return false;
    }
  }

  function railEligible() {
    return document.body.classList.contains('az-filters-left')
      && window.innerWidth >= MIN_WIDTH
      && !categoryFacetOff();
  }

  /* The app writes its own `c` param on the next response. This only clears a
     stale flat `category=` selection, the same thing its own handlers do. */
  function stripCategoryParam() {
    try {
      var u = new URL(window.location.href);
      if (!u.searchParams.has('category')) return;
      u.searchParams.delete('category');
      window.history.replaceState({}, '', u.toString());
    } catch (e) {}
  }

  /* ------------------------------------------------------------ navigation */

  /* Hand the move to the app the way its own back row does. `level` is where
     the target sits in the path, so a jump to a node the trail has never seen
     can rebuild what sits above it: the app reseeds an unfamiliar trail from
     parent alone and drops every ancestor above that parent. */
  function goTo(gid, name, level) {
    var g = grid();
    if (!g) return;

    g.currentCategoryId = gid || null;

    if (!gid) {
      g.categoryTrail = [];
    } else {
      var trail = Array.isArray(g.categoryTrail) ? g.categoryTrail : [];
      var at = trail.map(function (t) { return t.id; }).indexOf(gid);
      if (at >= 0) {
        g.categoryTrail = trail.slice(0, at + 1);
      } else if (typeof level === 'number' && level >= 0) {
        g.categoryTrail = trail.slice(0, level).concat([{ id: gid, name: name }]);
      }
    }

    stripCategoryParam();
    document.dispatchEvent(new CustomEvent('filterApplied', {
      detail: { filterType: 'category', selectedTypes: [] }
    }));
  }

  /* ------------------------------------------------------------ breadcrumb */

  /*
   * The breadcrumb goes to the top of the app block, not next to the heading.
   *
   * The heading lives inside atomz-sticky, inside the results column, so a
   * crumb built beside it was indented to where the products start and sat
   * level with the filter rail rather than above it. The collection page's
   * crumb is a full-width row above both columns, and a shopper moving between
   * the two pages sees the same line jump half a screen sideways.
   *
   * Only the position is taken. The trail, the links and what they do are
   * unchanged, and the node is created once and moved once: renderCrumb keeps
   * writing into whatever this returns.
   */
  function crumbHost() {
    var heading = document.querySelector('.az-results-heading');
    var block = document.querySelector('.az-app-block');
    if (!heading || !heading.parentNode) return null;

    var el = document.getElementById('az-crumb');
    if (!el) {
      el = document.createElement('nav');
      el.id = 'az-crumb';
      el.className = 'az-crumb';
      el.setAttribute('aria-label', 'Category');
      heading.parentNode.insertBefore(el, heading);
    }

    /* Moved on every pass rather than once: the app re-renders its own
       subtree on a refine, and a crumb that had been lifted out of it would
       otherwise be left behind the next time the block is rebuilt. */
    if (block && el.parentNode !== block) block.insertBefore(el, block.firstChild);

    return el;
  }

  function crumbLink(label, gid, level) {
    var a = document.createElement('button');
    a.type = 'button';
    a.className = 'az-crumb__link';
    a.textContent = label;
    a.addEventListener('click', function () { goTo(gid, label, level); });
    return a;
  }

  function renderCrumb() {
    var el = crumbHost();
    if (!el) return;

    if (!S.trail.length) { el.remove(); return; }

    el.innerHTML = '';

    /*
     * Home, and a real link to it, because that is what the collection page's
     * crumb says and a shopper reads the two pages as one site. It used to be
     * an "All" button that cleared the category and stayed in the search,
     * which is a different move wearing the same clothes: the first word of a
     * breadcrumb is where people expect the way out, not a filter reset. The
     * reset is still in the rail, as the Category group.
     */
    var home = document.createElement('a');
    home.className = 'az-crumb__link';
    home.href = '/';
    home.textContent = 'Home';
    el.appendChild(home);

    S.trail.forEach(function (node, i) {
      var sep = document.createElement('span');
      sep.className = 'az-crumb__sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '/';
      el.appendChild(sep);

      if (i === S.trail.length - 1) {
        /* The node you are standing on is a label, not a link. Making it
           clickable would offer a move that changes nothing. */
        var here = document.createElement('span');
        here.className = 'az-crumb__here';
        here.setAttribute('aria-current', 'page');
        here.textContent = titleCase(node.name);
        el.appendChild(here);
      } else {
        el.appendChild(crumbLink(titleCase(node.name), node.id, i));
      }
    });
  }

  /* ----------------------------------------------------------------- title */

  /*
   * The app's heading is "Results for <query>", which is the words still
   * sitting in the search box a few hundred pixels above it, set larger than
   * anything else on the page including the products. Inside a category there
   * is something better to put there: the category.
   *
   * The count is off, to match the collection page and the homepage, which
   * both stopped printing theirs. "Sandals 792 items" is not an invitation to
   * a shopper who wants one pair; it is the size of the pile they have to get
   * through. The mirror stays wired up behind this flag rather than being
   * pulled out, because the machinery is the part that was hard: the app
   * replaces its own count node on every refine, so the observer in
   * watchCount is what keeps any future count honest.
   */
  var SHOW_COUNT = false;

  function paintCount() {
    var host = document.getElementById('az-title');
    var slot = host && host.querySelector('.az-title__count');
    if (slot) slot.textContent = SHOW_COUNT ? countText() : '';
  }

  /* The count arrives after the heading is built and changes again on every
     refine, so mirroring it once is not enough. Re-attach when the app
     replaces the node rather than assuming the first one survives. */
  var countWatch = null;
  var countWatched = null;

  function watchCount() {
    var el = countNode();
    if (!el || el === countWatched) return;
    if (countWatch) countWatch.disconnect();
    countWatched = el;
    countWatch = new MutationObserver(paintCount);
    countWatch.observe(el, { childList: true, characterData: true, subtree: true });
  }

  function renderTitle() {
    var heading = document.querySelector('.az-results-heading');
    var el = document.getElementById('az-title');
    var here = S.trail.length ? S.trail[S.trail.length - 1] : null;
    var label = here ? titleCase(here.name) : currentQuery();

    if (!label || !heading || !heading.parentNode) {
      if (el) el.remove();
      document.body.classList.remove('az-has-title');
      return;
    }

    if (!el) {
      el = document.createElement('h1');
      el.id = 'az-title';
      el.className = 'az-title';
      heading.parentNode.insertBefore(el, heading);
    }

    /* Rebuilt rather than assumed: the element is only ever created here, but
       a half-built one would throw on the next line and take the breadcrumb
       and the rail down with it. */
    if (!el.querySelector('.az-title__name')) {
      el.innerHTML = '';
      el.appendChild(document.createElement('span')).className = 'az-title__name';
      el.appendChild(document.createElement('span')).className = 'az-title__count';
    }
    el.querySelector('.az-title__name').textContent = label;

    document.body.classList.add('az-has-title');
    watchCount();
    paintCount();
  }

  /* ------------------------------------------------------- suggestion chips */

  /*
   * The app heads this block "Look for these instead", which describes a
   * correction. The rows under it are narrower queries - Adidas Black
   * Sneakers, Cushioned Adidas Sneakers - so it is a refinement rail, and it
   * belongs under the title where a shopper looks next rather than below the
   * grid where an apology would go.
   *
   * Moved rather than reordered. `order` only applies inside a flex or grid
   * container, and .az-app-block takes its display from three different files
   * depending on layout and width. One of them is `display: block`, which
   * makes every order value in the stack inert at once, and the earlier CSS
   * approach half-applied: the divider and spacing landed, the position did
   * not. Moving the node does not care what the container is.
   */
  var CHIPS_LABEL = 'Narrow it down';

  function moveSuggestions() {
    var head = document.querySelector('.az-suggestions-heading');
    var body = document.querySelector('.az-sticky-suggestions');
    var anchor = document.getElementById('az-title') || document.getElementById('az-crumb');
    if (!head || !body || !anchor || !anchor.parentNode) return;

    if (head.textContent !== CHIPS_LABEL) head.textContent = CHIPS_LABEL;

    /*
     * The app nests .az-results-heading, and so #az-title, INSIDE
     * .az-sticky-suggestions:
     *
     *   <h2 class="az-suggestions-heading">
     *   <atomz-sticky class="az-sticky-suggestions">
     *     <div id="az-suggestions">        the pills
     *     <h3 class="az-results-heading">  becomes #az-title
     *
     * Moving that wrapper to sit after its own descendant throws
     * HierarchyRequestError, and the throw took renderRail() down with it on
     * the next line, so the chips stranded themselves above the breadcrumb and
     * the category group never drew at all. Move the pill list rather than the
     * wrapper whenever the anchor is inside it.
     */
    var moving = body.contains(anchor) ? body.querySelector('.az-suggestions') : body;
    if (!moving || moving === anchor || moving.contains(anchor)) return;

    var p = anchor.parentNode;
    if (anchor.nextSibling !== head) p.insertBefore(head, anchor.nextSibling);
    if (head.nextSibling !== moving) p.insertBefore(moving, head.nextSibling);
  }

  /* ------------------------------------------------------------- rail group */

  function railHost() {
    var w = document.querySelector('.az-filter-wrapper');
    if (!w) return null;
    var el = document.getElementById('az-tax');
    if (!el) {
      el = document.createElement('section');
      el.id = 'az-tax';
      el.className = 'az-tax';
      /* Above the filter groups but below Clear All, and it stays there: the
         app's reorderFilterGroups shuffles by appendChild and only ever touches
         nodes carrying .az-filter, which this deliberately does not. Group order
         itself is the API's business, not this file's. */
      w.insertBefore(el, w.querySelector('.az-filter'));
    }
    return el;
  }

  function railRow(kid) {
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'az-tax__row';

    var name = document.createElement('span');
    name.className = 'az-tax__name';
    name.textContent = titleCase(kid.value);
    el.appendChild(name);

    if (kid.count != null && kid.count !== '') {
      var n = document.createElement('span');
      n.className = 'az-tax__count';
      n.textContent = kid.count;
      el.appendChild(n);
    }

    /* No radio and no checkbox. Picking one of these moves the page rather than
       ticking a box, and a control that can never be found in its checked state
       would only be describing something the page does not do. */
    el.addEventListener('click', function () {
      goTo(kid.gid, kid.value, S.trail.length);
    });

    return el;
  }

  function renderRail() {
    var el = document.getElementById('az-tax');

    /*
     * The takeover flag says the category UI is ours, not that there is a group
     * to draw. At a leaf there is no next step and so no group, but the app's
     * dropdown must stay down: all it could offer there is the current node and
     * a back row, which is the breadcrumb again in checkbox form.
     */
    document.body.classList.toggle('az-tax-on', S.on && railEligible());

    if (!railEligible() || !S.kids.length) {
      if (el) el.remove();
      return;
    }

    el = railHost();
    if (!el) return;
    el.innerHTML = '';

    var head = document.createElement('div');
    head.className = 'az-tax__head';
    head.textContent = 'Category';
    el.appendChild(head);

    var body = document.createElement('div');
    body.className = 'az-tax__body';

    var shown = S.more ? S.kids : S.kids.slice(0, COLLAPSE_AFTER);
    shown.forEach(function (kid) { body.appendChild(railRow(kid)); });

    if (!S.more && S.kids.length > COLLAPSE_AFTER) {
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'az-tax__more';
      more.textContent = 'Show ' + (S.kids.length - COLLAPSE_AFTER) + ' more';
      more.addEventListener('click', function () { S.more = true; renderRail(); });
      body.appendChild(more);
    }

    el.appendChild(body);
  }

  /*
   * Each step is independent, and the four are worth having separately. A
   * throw inside one used to silently drop every step after it, which is how a
   * single insertBefore error cost the rail its category group.
   */
  function render() {
    [renderCrumb, renderTitle, moveSuggestions, renderRail].forEach(function (step) {
      try { step(); } catch (e) { /* keep the remaining steps */ }
    });
  }

  function teardown() {
    var c = document.getElementById('az-crumb'); if (c) c.remove();
    var h = document.getElementById('az-title'); if (h) h.remove();
    var t = document.getElementById('az-tax'); if (t) t.remove();
    document.body.classList.remove('az-tax-on', 'az-has-title');
    S.on = false;
  }

  /* ----------------------------------------------------------------- wire */

  document.addEventListener('searchPerformed', function (e) {
    var d = e && e.detail;

    /*
     * No taxonomy in the response. The breadcrumb and the category group have
     * nothing to draw, but the header line and the chips are not taxonomy
     * features: the line falls back to the query, and the chips are the app's
     * own. Tearing all four down would have made a plain search look like a
     * different page.
     */
    if (!d || !d.hierarchical) {
      teardown();
      renderTitle();
      moveSuggestions();
      return;
    }

    var g = grid();
    var payload = productsPayload();
    var derived = payload ? trailFromCurrent(payload.currentCategory) : null;

    if (payload && !payload.currentCategory) {
      S.trail = [];
      if (g) g.categoryTrail = [];
    } else if (derived) {
      S.trail = derived;
      /* Hand the full path to the app as well. It only reads and truncates this
         field, and with every ancestor present its truncation branch runs
         instead of its reseed branch, so its own back row stops skipping
         levels. */
      if (g) g.categoryTrail = derived.slice();
    } else {
      S.trail = (g && Array.isArray(g.categoryTrail)) ? g.categoryTrail.slice() : [];
      var back = d.categoryBack;
      if (back && back.id && !S.trail.some(function (t) { return t.id === back.id; })) {
        S.trail.unshift({ id: back.id, name: back.name });
        if (g) g.categoryTrail = S.trail.slice();
      }
    }

    S.kids = (Array.isArray(d.category) ? d.category : []).filter(function (r) {
      return !r.applied;
    });
    S.more = false;
    S.on = true;
    render();
  });

  var resizeTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () { if (S.on) render(); }, 150);
  });
})();
